const airportTemplate = (slug, name, icao, iata, subtitle, introText, expertText, keyFacts, realityTitle, realityText, tips) => ({
  slug,
  cluster: "Airport Guide",
  metaTitle: `Private Jet at ${name} ${icao} — Guide | Gate 33`,
  metaDescription: `${name} (${icao}) private aviation guide. Catering, FBO operations, and crew essentials for business jet flights.`,
  title: `Flying Private from ${name}`,
  subtitle,
  introTitle: `${name}: Business Aviation Operations`,
  introText,
  expertTitle: `Operating at ${icao}`,
  expertText,
  expertListTitle: `${icao} Key Facts`,
  expertList: keyFacts,
  realityTitle,
  realityText,
  practicalTitle: `Crew Essentials for ${icao}`,
  practicalItems: tips,
  ctaTitle: `Get ${name}-Optimized Catering`,
  ctaText: `GalleyPilot adapts catering orders to your departure airport. Local caterer intelligence for ${name} and beyond.`,
  ctaPrimary: "catering"
});

export const airportExtraPages = [
  airportTemplate(
    "ibiza-leib-private-jet", "Ibiza", "LEIB", "IBZ",
    "LEIB — The Mediterranean party island that transforms into a UHNW playground every summer. Peak season demands peak preparation.",
    [
      "Ibiza Airport handles enormous private aviation traffic from May through October. The island attracts UHNW clients, celebrities, and corporate retreats, all expecting flawless service despite the island's relaxed reputation.",
      "For crew, Ibiza means high expectations, limited catering infrastructure compared to major hubs, and the logistical challenges of a small island during peak season."
    ],
    [
      "Ibiza's business aviation operations are concentrated in a relatively small terminal. During peak summer weekends, the ramp is packed and ground handling times extend significantly.",
      "Catering options on the island are more limited than mainland hubs. Many crew pre-order from Palma or Barcelona caterers for complex requirements."
    ],
    [
      "ICAO: LEIB | IATA: IBZ",
      "Runway: 2,800m — handles most business jet types",
      "Peak season: May–October (extreme July–August)",
      "Limited local aviation catering — plan ahead or source from mainland",
      "Mediterranean cuisine focus: seafood, paella, fresh local produce",
      "30 minutes from major beach clubs and hotels",
      "Noise restrictions may apply — check NOTAMs"
    ],
    "The Island Supply Chain Challenge",
    [
      "Everything on Ibiza is imported or locally produced in limited quantities. During peak season, caterers are stretched thin and premium items may be unavailable at short notice.",
      "The crew who operate smoothly in Ibiza are the ones who order 72 hours ahead, have backup menus ready, and maintain relationships with multiple suppliers."
    ],
    [
      { title: "Order 72 hours ahead in summer", description: "Peak season Ibiza has limited catering capacity. Early ordering is essential for quality and availability." },
      { title: "Mediterranean menus work best", description: "Fresh seafood, local fruits, Spanish wines. Lean into the island's culinary strengths rather than fighting the supply chain." },
      { title: "Heat management critical", description: "Mediterranean summer heat demands strict cold chain discipline. Load catering with AC running." },
      { title: "Ground transport coordination", description: "Ibiza's roads are narrow and congested in summer. Coordinate FBO ground transport well in advance." }
    ]
  ),
  airportTemplate(
    "mykonos-lgmk-private-jet", "Mykonos", "LGMK", "JMK",
    "LGMK — The Greek island jewel of private aviation. Small runway, big expectations, and Mediterranean catering at its finest.",
    [
      "Mykonos Airport is one of the most challenging and rewarding business aviation destinations in Europe. The short runway limits aircraft types, but the UHNW clientele demands the highest standards.",
      "Greek hospitality meets business aviation luxury here. The catering opportunities — fresh Mediterranean cuisine, exceptional olive oil, local wines — are outstanding."
    ],
    [
      "Mykonos operations require specific aircraft knowledge. The runway (1,400m) limits operations to light and some super-mid jets. Larger aircraft must position from Athens or nearby islands.",
      "Local catering is limited but high-quality. Greek cuisine translates beautifully to in-flight service when ordered correctly."
    ],
    [
      "ICAO: LGMK | IATA: JMK",
      "Runway: 1,400m — light jets and some super-mids only",
      "Peak season: June–September",
      "Limited FBO infrastructure — plan for basic facilities",
      "Greek Mediterranean cuisine: exceptional fresh ingredients",
      "Wind: Meltemi winds can affect operations July–August",
      "Close proximity to Athens LGAV for positioning"
    ],
    "The Short Runway Reality",
    [
      "Mykonos's 1,400m runway means many popular business jets cannot operate here. Clients expecting their usual G650 or Global will need to position from Athens. Brief the operations team early.",
      "Weather is the other factor. Meltemi winds blow strongly in July and August, occasionally making approaches challenging or impossible."
    ],
    [
      { title: "Know your runway limits", description: "Verify aircraft performance for LGMK's short runway. Brief clients on alternative positioning if their aircraft type is restricted." },
      { title: "Greek cuisine excellence", description: "Fresh Greek salad, grilled octopus, local feta, excellent olive oil. Mediterranean simplicity at its finest — perfect for in-flight service." },
      { title: "Wind awareness", description: "Meltemi winds can delay or divert flights. Have contingency plans and keep passengers informed." },
      { title: "Limited ground facilities", description: "Mykonos FBO facilities are basic compared to major hubs. Manage expectations and plan for self-sufficiency." }
    ]
  ),
  airportTemplate(
    "sardinia-olbia-lieo-private-jet", "Sardinia Olbia", "LIEO", "OLB",
    "LIEO — The Costa Smeralda gateway. Italian luxury meets business aviation in one of the Mediterranean's most exclusive destinations.",
    [
      "Olbia Costa Smeralda Airport serves one of the world's most exclusive resort destinations. During summer, the airport handles a significant volume of UHNW private aviation traffic heading to Porto Cervo and the Aga Khan's legendary coastline.",
      "Italian catering excellence is the standard here. Fresh pasta, Sardinian specialties, and exceptional wines make in-flight dining a highlight."
    ],
    [
      "Olbia's private aviation terminal is well-equipped during peak season, with dedicated handling for business jets. The Italian approach to hospitality extends to every aspect of ground operations.",
      "Catering from Sardinia benefits from the island's exceptional food culture: Pane Carasau, pecorino, fresh seafood, and Cannonau wines."
    ],
    [
      "ICAO: LIEO | IATA: OLB",
      "Runway: 2,445m — handles most business jet categories",
      "Peak season: June–September (extreme July–August)",
      "Gateway to Costa Smeralda and Porto Cervo",
      "Italian/Sardinian cuisine: exceptional local specialties",
      "Well-equipped private aviation terminal",
      "Helicopter transfers available to remote coastal villas"
    ],
    "The Costa Smeralda Standard",
    [
      "Costa Smeralda clients are among the wealthiest in the world. They're arriving from — or heading to — some of the most exclusive properties and yachts in the Mediterranean. Service expectations match that context.",
      "Crew operating through Olbia in peak season should expect demanding, sophisticated clients who are deeply familiar with luxury service standards."
    ],
    [
      { title: "Italian catering mastery", description: "Fresh pasta, Sardinian pecorino, grilled local seafood. Italian cuisine translates perfectly to in-flight service. Source locally for authenticity." },
      { title: "Wine selection matters", description: "Sardinian Cannonau, Vermentino di Gallura, or pan-Italian selections. Clients expect quality wine knowledge." },
      { title: "Yacht and villa coordination", description: "Many clients transfer directly to yachts or villas. Coordinate with the client's team for seamless ground transitions." },
      { title: "Summer heat management", description: "Sardinian summers are hot. Standard Mediterranean cold chain protocols apply. Load catering with AC running." }
    ]
  ),
  airportTemplate(
    "marrakech-gmmx-private-jet", "Marrakech", "GMMX", "RAK",
    "GMMX — Morocco's jewel city. Where North African hospitality meets business aviation and spice-market gastronomy.",
    [
      "Marrakech Menara Airport serves as Morocco's primary business aviation gateway. The city attracts UHNW clients for its luxury riads, desert experiences, and vibrant culture.",
      "Moroccan catering offers unique in-flight opportunities: tagines, pastilla, fresh mint tea, and aromatic spices that create memorable service experiences."
    ],
    [
      "Marrakech operations are well-established for business aviation. The airport handles significant private jet traffic, particularly during winter months when European and Gulf clients seek warmer destinations.",
      "Ground handling is professional, and local catering can deliver authentic Moroccan cuisine at excellent quality — but requires advance ordering for complex requests."
    ],
    [
      "ICAO: GMMX | IATA: RAK",
      "Runway: 3,100m — handles all business jet types",
      "Peak season: October–April (European winter escape)",
      "Moroccan cuisine: tagine, couscous, pastilla, fresh mint tea",
      "Professional ground handling with business aviation experience",
      "40 minutes from the Medina and luxury riads",
      "Year-round warm climate — heat management in summer months"
    ],
    "Cultural Catering Intelligence",
    [
      "Marrakech's catering market excels in authentic Moroccan cuisine but may be limited in international options compared to European hubs. For clients wanting French or Italian food, source from local upscale restaurants rather than aviation caterers.",
      "Understanding Moroccan cultural nuances — Halal requirements, traditional tea service protocols, and appropriate hospitality gestures — enhances the client experience significantly."
    ],
    [
      { title: "Embrace Moroccan cuisine", description: "Authentic tagines, pastilla, and Moroccan salads create memorable departures. Work with local caterers who specialize in premium Moroccan gastronomy." },
      { title: "Mint tea service", description: "Traditional Moroccan mint tea service is a beautiful in-flight ritual. Learn the proper pouring technique — it's a mark of cultural respect." },
      { title: "Halal by default", description: "Morocco is a Muslim country. All local catering is Halal. For clients requiring non-Halal items, arrange specifically." },
      { title: "Spice management", description: "Moroccan food is aromatic. Consider cabin air quality when serving strongly spiced dishes. Some passengers love it; others find it overwhelming." }
    ]
  ),
  airportTemplate(
    "aspen-kase-private-jet", "Aspen", "KASE", "ASE",
    "KASE — The ski resort runway that tests every pilot's skill. America's most exclusive mountain destination with unique operational challenges.",
    [
      "Aspen-Pitkin County Airport is one of the most challenging airports in US business aviation. Nestled in the Rocky Mountains at 7,820 feet elevation, with a single runway and strict noise abatement procedures, it demands exceptional planning.",
      "The clientele is ultra-elite — billionaires, celebrities, and corporate leaders escaping to one of America's most exclusive resort towns."
    ],
    [
      "Aspen operations require specific aircraft certification and crew training. Not all aircraft or pilots are approved for KASE approaches. Weather windows can be tight, and diversions to Rifle, Eagle, or Grand Junction are common.",
      "Catering must account for both mountain logistics and the expectations of America's wealthiest clientele."
    ],
    [
      "ICAO: KASE | IATA: ASE",
      "Elevation: 7,820 ft — significant performance considerations",
      "Runway: 8,006 ft — but altitude reduces effective length",
      "Strict noise abatement and curfew procedures",
      "Weather-dependent operations — common diversions",
      "Peak seasons: winter (ski) and summer (outdoor)",
      "Limited ramp space — parking fills fast during holidays"
    ],
    "Mountain Operations Reality",
    [
      "Aspen is not a fly-in-and-forget airport. Weather changes rapidly, approaches are challenging, and diversions happen regularly. Clients must be briefed on the possibility of diverting to an alternate airport and driving the remaining distance.",
      "Crew should always have contingency catering and supplies for extended ground time in case of weather holds."
    ],
    [
      { title: "Altitude affects catering", description: "At 7,820 ft, food behaves differently. Packaging may expand, carbonated beverages fizz more. Account for altitude in your preparation." },
      { title: "Winter comfort food", description: "Ski-season clients want hearty, warming food. Soups (in spill-proof containers), hot chocolate, and comfort meals work perfectly." },
      { title: "Plan for diversions", description: "Always have snacks and water for extended ground time. Diversions to Rifle or Eagle mean 1-2 hours of additional driving." },
      { title: "Holiday rush", description: "Christmas and New Year weeks are extreme. Everything books out months ahead. Order catering as early as possible." }
    ]
  ),
  airportTemplate(
    "cannes-mandelieu-lfmd-private-jet", "Cannes", "LFMD", "CEQ",
    "LFMD — The Cannes Film Festival runway. French Riviera glamour meets business aviation operational precision.",
    [
      "Cannes-Mandelieu Airport is the closest private aviation airport to Cannes itself. During the Film Festival and MIPIM, it becomes one of the busiest business aviation airports in Europe.",
      "The Riviera setting demands French Mediterranean catering excellence — fresh, elegant, and perfectly timed."
    ],
    [
      "LFMD is a smaller airport with a single runway and limited ramp space. During major events, operations become intense with back-to-back movements and tight parking.",
      "Local caterers understand the Riviera market well, offering premium French Mediterranean cuisine adapted for in-flight service."
    ],
    [
      "ICAO: LFMD | IATA: CEQ",
      "Runway: 1,610m — limits operations to light and some super-mid jets",
      "Alternative: Nice LFMN for larger aircraft",
      "Peak events: Cannes Film Festival (May), MIPIM (March)",
      "French Riviera catering: Mediterranean excellence",
      "10 minutes from central Cannes, 40 from Nice",
      "Limited parking during major events — book ramp space early"
    ],
    "Festival Season Chaos",
    [
      "During the Cannes Film Festival, LFMD operates at absolute capacity. Every ramp position is filled, ground handling times extend, and catering deliveries compete with dozens of other operations.",
      "If your flight coincides with a major event, plan everything with double the usual lead time. Book your slot, your parking, and your catering as far in advance as possible."
    ],
    [
      { title: "Runway limitations", description: "1,610m runway limits aircraft types. Larger jets must use Nice LFMN. Verify your aircraft's performance before planning." },
      { title: "Event-period planning", description: "During Film Festival or MIPIM, book everything weeks in advance. Last-minute is not possible during major events." },
      { title: "French Riviera menus", description: "Fresh Mediterranean cuisine, Provençal specialties, local rosé. Match the Riviera setting with appropriate catering." },
      { title: "Ground transport", description: "Cannes is close, but traffic during events is severe. Coordinate car service well in advance." }
    ]
  ),
  airportTemplate(
    "malaga-lemg-private-jet", "Malaga", "LEMG", "AGP",
    "LEMG — Southern Spain's gateway to Marbella, the Costa del Sol, and one of Europe's fastest-growing business aviation markets.",
    [
      "Malaga Airport serves as the primary business aviation gateway to Marbella and the Costa del Sol. The market has grown significantly, driven by UHNW clients relocating to southern Spain and the region's year-round appeal.",
      "Spanish catering traditions — tapas, Iberian ham, excellent wines — create wonderful in-flight service opportunities."
    ],
    [
      "Malaga's business aviation terminal has modernized rapidly. The airport handles growing private jet traffic efficiently, with professional ground handling and improving catering options.",
      "The Costa del Sol's international community means catering demand spans Spanish, British, Scandinavian, and Middle Eastern cuisines."
    ],
    [
      "ICAO: LEMG | IATA: AGP",
      "Runway: 3,200m — handles all business jet types",
      "Year-round destination with peaks in summer and winter",
      "Gateway to Marbella (45 min) and Puerto Banús",
      "Spanish Mediterranean cuisine with international options",
      "Growing business aviation infrastructure",
      "Helicopter transfers available to Marbella and surrounding areas"
    ],
    "The Marbella Factor",
    [
      "Marbella's UHNW community has its own expectations shaped by the destination's luxury culture. Many clients are regulars who fly the same routes repeatedly. Consistency and personalization are highly valued.",
      "The drive from Malaga Airport to Marbella takes 45 minutes minimum — coordinate ground transport carefully."
    ],
    [
      { title: "Spanish gastronomy", description: "Iberian jamón, manchego, fresh seafood, excellent Rioja and Albariño wines. Spanish cuisine is perfect for in-flight service." },
      { title: "Tapas-style service", description: "For shorter flights, a premium tapas selection works beautifully. Multiple small dishes create an engaging, social dining experience." },
      { title: "Year-round operations", description: "Unlike some Mediterranean destinations, Malaga operates year-round. Winter clients often prefer warm, hearty Spanish cuisine." },
      { title: "International catering needs", description: "The Costa del Sol's diverse community means you may need non-Spanish cuisines. Verify local caterer capabilities for international requests." }
    ]
  ),
  airportTemplate(
    "st-barths-tffj-private-jet", "St Barths", "TFFJ", "SBH",
    "TFFJ — The Caribbean's most exclusive (and most challenging) private aviation destination. A 650m runway that defines operational expertise.",
    [
      "Gustaf III Airport on St Barthélemy has one of the shortest and most challenging runways in the world. At just 650 meters, with a hilltop approach and beach-end departure, it's a destination that tests every operation.",
      "The clientele is ultra-exclusive — the Caribbean's wealthiest visitors who expect paradise-level service despite the logistical constraints."
    ],
    [
      "St Barths operations are unlike anything else in business aviation. Only specific aircraft types and specially trained pilots can operate here. Most clients arrive via connecting flights from St Martin (SXM) on small turboprops.",
      "Catering must be sourced from St Martin or brought from the departure point. Local options on St Barths are limited for aviation catering."
    ],
    [
      "ICAO: TFFJ | IATA: SBH",
      "Runway: 650m — one of the world's shortest commercial runways",
      "Only small turboprops and specific aircraft types approved",
      "Most business jet clients connect via St Martin SXM",
      "Caribbean cuisine influenced by French culinary tradition",
      "Peak season: December–April (Caribbean winter)",
      "Ultra-exclusive clientele — New Year's Eve is the peak of peaks"
    ],
    "The 650-Meter Challenge",
    [
      "St Barths is not a destination for most business jets. The 650m runway means clients typically fly their jet to St Martin (SXM) and transfer to a turboprop for the 10-minute hop to St Barths.",
      "Crew managing this transition must coordinate catering for both legs, manage luggage transfers, and ensure the client experience feels seamless despite the aircraft change."
    ],
    [
      { title: "Plan the SXM connection", description: "Most clients need a turboprop transfer from St Martin. Coordinate timing, luggage, and catering continuity between aircraft." },
      { title: "French Caribbean cuisine", description: "St Barths blends French gastronomy with Caribbean ingredients. Fresh lobster, tropical fruits, and French wines create an exceptional catering experience." },
      { title: "New Year's period", description: "The last two weeks of December and first week of January are the most intense period. Everything books out months ahead." },
      { title: "Bring catering from departure", description: "Don't rely on sourcing complex catering on the island. Bring quality provisions from your departure point or from St Martin." }
    ]
  ),
  airportTemplate(
    "chambery-lflb-private-jet", "Chambéry", "LFLB", "CMF",
    "LFLB — The gateway to the French Alps. Ski season transforms this quiet regional airport into a business aviation hotspot.",
    [
      "Chambéry Airport is the closest airport to Courchevel, Méribel, and Val d'Isère — the French Alps' most exclusive ski resorts. During winter season, it handles significant private aviation traffic.",
      "Operations are weather-dependent and season-focused. Crew flying into Chambéry need alpine operational awareness and winter-appropriate catering."
    ],
    [
      "Chambéry's business aviation operations are concentrated in the December–April ski season. The airport has adequate private aviation handling during peak periods, but capacity is limited.",
      "Catering options are more limited than major hubs. Many crew pre-order from Geneva or Lyon caterers and have deliveries timed for Chambéry arrivals."
    ],
    [
      "ICAO: LFLB | IATA: CMF",
      "Runway: 2,020m — adequate for most super-mid and below",
      "Peak season: December–April (ski season)",
      "Gateway to Courchevel, Méribel, Val d'Isère",
      "Weather-dependent: mountain conditions can change rapidly",
      "Limited local aviation catering — source from Geneva or Lyon",
      "Helicopter transfers available to ski resorts"
    ],
    "Alpine Weather Uncertainty",
    [
      "Mountain airports mean mountain weather. Chambéry can go from clear to socked-in within an hour. Diversions to Lyon or Geneva are common, especially in January and February.",
      "Always have contingency plans for weather diversions. Brief clients that alpine operations carry inherent uncertainty."
    ],
    [
      { title: "Winter comfort catering", description: "Ski clients want warming food. Hot soups (in sealed containers), rich stews, fondue elements, and hot chocolate create perfect alpine experiences." },
      { title: "Source from Geneva/Lyon", description: "For complex catering orders, use Geneva or Lyon caterers. Local Chambéry options are limited for premium aviation catering." },
      { title: "Weather contingency", description: "Always have snacks and water for extended holds or diversions. Alpine weather is the single biggest operational variable." },
      { title: "Helicopter coordination", description: "Many ski clients transfer to helicopter for the final leg to resort. Coordinate timing and luggage transfer through the FBO." }
    ]
  ),
  airportTemplate(
    "innsbruck-lowi-private-jet", "Innsbruck", "LOWI", "INN",
    "LOWI — The Austrian Alps' most dramatic approach. Challenging mountain operations with Tyrolean hospitality and winter sports clientele.",
    [
      "Innsbruck Airport sits in a narrow alpine valley surrounded by mountains on all sides. The approach is one of the most visually dramatic — and operationally challenging — in European aviation.",
      "Serving Austria's premier ski region and the historic city of Innsbruck, the airport handles significant business aviation traffic during both winter and summer seasons."
    ],
    [
      "Innsbruck operations require specific crew and pilot qualifications due to the challenging approach profile. Not all operators are approved for LOWI, and weather cancellations are more frequent than at flatland airports.",
      "Austrian hospitality standards are high, and local catering reflects the region's excellent culinary traditions — Tyrolean specialties, excellent pastries, and Austrian wines."
    ],
    [
      "ICAO: LOWI | IATA: INN",
      "Runway: 2,000m — in a narrow alpine valley",
      "Special approach procedures — pilot qualification required",
      "Peak seasons: winter (ski) and summer (hiking, culture)",
      "Austrian/Tyrolean cuisine: hearty, excellent pastries, fine wines",
      "Professional business aviation handling",
      "Direct access to Tyrolean ski resorts and Innsbruck city"
    ],
    "The Valley Approach",
    [
      "Innsbruck's valley approach is not for the faint-hearted. Mountains on both sides, step-down approaches, and rapidly changing mountain weather make this one of Europe's most operationally demanding airports.",
      "Crew should always verify that their pilots are LOWI-qualified and that weather conditions are within limits. Diversions to Munich are the standard contingency."
    ],
    [
      { title: "Austrian alpine cuisine", description: "Tyrolean specialties — Kaiserschmarrn, Tiroler Gröstl, excellent Apfelstrudel. Austrian cuisine provides wonderful winter comfort food for in-flight service." },
      { title: "Weather first", description: "Mountain weather takes priority over schedules. Build generous buffers and have Munich as your standard diversion airport." },
      { title: "Pastry excellence", description: "Austrian pastry tradition is world-class. Fresh Strudel, Sachertorte, and Krapfen make excellent in-flight treats." },
      { title: "Summer operations", description: "Innsbruck is increasingly popular for summer hiking and cultural tourism. Lighter, fresher menus work better in warmer months." }
    ]
  ),
  airportTemplate(
    "sao-paulo-sbsp-private-jet", "São Paulo Congonhas", "SBSP", "CGH",
    "SBSP — Brazil's busiest executive aviation airport. Latin American business hub with extraordinary culinary diversity.",
    [
      "São Paulo Congonhas is one of the busiest executive aviation airports in the world. Brazil's business capital generates enormous private jet traffic, both domestic and international.",
      "Brazilian catering offers unique opportunities: tropical fruits, excellent Brazilian beef, creative fusion cuisine, and the vibrant food culture of South America's largest city."
    ],
    [
      "Congonhas operations are intense. The airport handles both commercial and business aviation in a constrained space. Slot management is strict, and ground handling requires advance coordination.",
      "Brazilian business aviation infrastructure is well-developed, reflecting the country's status as one of the world's largest private aviation markets."
    ],
    [
      "ICAO: SBSP | IATA: CGH",
      "Located in central São Paulo — minimal ground transfer time",
      "One of the world's busiest GA airports",
      "Brazilian business aviation infrastructure: well-developed",
      "Extraordinary culinary scene: Brazilian, Japanese, Italian influences",
      "Tropical climate year-round",
      "Strict slot management — file early"
    ],
    "The Brazilian Aviation Market",
    [
      "Brazil is one of the world's largest private aviation markets by fleet size. The market is sophisticated, and clients are experienced private jet users with established expectations.",
      "São Paulo's culinary scene rivals any global city. Japanese, Italian, and Brazilian fusion cuisines are all available at exceptional quality through aviation caterers."
    ],
    [
      { title: "Brazilian culinary excellence", description: "Beyond steakhouse stereotypes — São Paulo offers world-class Japanese, Italian, and modern Brazilian cuisine. Explore the city's culinary diversity." },
      { title: "Tropical fruit advantage", description: "Fresh tropical fruits — açaí, passion fruit, guava, mango — create stunning in-flight presentations unavailable at northern airports." },
      { title: "Coffee culture", description: "Brazilian coffee is exceptional. Source premium local beans. Your passengers will notice and appreciate the quality." },
      { title: "Slot discipline", description: "Congonhas is congested. File slots early, confirm handling, and build time buffers for taxi delays." }
    ]
  ),
  airportTemplate(
    "mexico-city-mmmx-private-jet", "Mexico City", "MMMX", "MEX",
    "MMMX — Latin America's aviation capital at 7,380 feet. High altitude, rich culture, and exceptional gastronomy.",
    [
      "Mexico City International Airport serves as Latin America's largest aviation hub. Business aviation operations are significant, reflecting Mexico's role as a major economic power.",
      "Mexican gastronomy — recognized by UNESCO as Intangible Cultural Heritage — offers extraordinary catering possibilities that no other destination can match."
    ],
    [
      "Mexico City operations at 7,380 feet elevation affect both aircraft performance and food preparation. The altitude is a real operational factor that crew must account for.",
      "Local aviation catering draws from Mexico City's legendary food scene, offering everything from traditional Mexican haute cuisine to international fine dining."
    ],
    [
      "ICAO: MMMX | IATA: MEX",
      "Elevation: 7,380 ft — significant performance considerations",
      "Latin America's largest aviation hub",
      "Mexican gastronomy: UNESCO Intangible Cultural Heritage",
      "Business aviation FBOs with full-service capabilities",
      "Year-round mild climate at altitude",
      "Complex airspace — ATC coordination important"
    ],
    "Altitude Affects Everything",
    [
      "At 7,380 feet, Mexico City's altitude affects aircraft performance, cooking temperatures, and food behavior. Packaging expands, water boils at lower temperatures, and carbonated beverages react differently.",
      "Crew must factor altitude into every aspect of their galley management and catering planning when operating from MMMX."
    ],
    [
      { title: "Mexican culinary excellence", description: "Mexico City's food scene is world-class. From traditional mole to modern Mexican haute cuisine, the catering possibilities are extraordinary." },
      { title: "Altitude awareness", description: "Account for 7,380 ft elevation in catering. Sealed containers may expand. Adjust oven times. Test before serving." },
      { title: "Tequila and mezcal", description: "Premium Mexican spirits are exceptional. A curated selection of artisanal tequila or mezcal elevates any departure service." },
      { title: "Security considerations", description: "Follow operator security protocols for Mexico City operations. Coordinate ground transport through established, vetted providers." }
    ]
  ),
  airportTemplate(
    "lagos-dnmm-private-jet", "Lagos", "DNMM", "LOS",
    "DNMM — West Africa's business aviation gateway. Rapid market growth, unique operational challenges, and emerging catering infrastructure.",
    [
      "Lagos Murtala Muhammed Airport is the primary gateway for private aviation in West Africa. Nigeria's economic capital generates significant business jet traffic, driven by oil, finance, and telecommunications industries.",
      "Operating in Lagos requires specific knowledge: security protocols, ground handling procedures, customs processes, and a growing but still developing catering infrastructure."
    ],
    [
      "Lagos business aviation operations have improved significantly in recent years, with dedicated FBOs offering international-standard facilities. However, the operational environment remains more complex than European or US airports.",
      "Catering is improving but still limited compared to major hubs. Many operators source catering from European or Middle Eastern stopovers for Lagos-originating flights."
    ],
    [
      "ICAO: DNMM | IATA: LOS",
      "West Africa's primary business aviation hub",
      "Dedicated business aviation terminals with improving facilities",
      "Growing market driven by Nigerian business sector",
      "Tropical climate year-round — heat management essential",
      "Catering: improving local options, many operators source from stopovers",
      "Security protocols: follow operator guidelines strictly"
    ],
    "Operating in West Africa",
    [
      "Lagos operations require patience, flexibility, and strong advance planning. Ground handling, customs, and immigration processes can be unpredictable. Build generous time buffers into every aspect of your operation.",
      "The market is growing rapidly, and standards are improving. But crew flying into Lagos for the first time should prepare for a different operational tempo than European or US airports."
    ],
    [
      { title: "Advance catering planning", description: "For complex catering orders, source from your departure point or a European/Middle Eastern stopover. Local premium aviation catering is limited." },
      { title: "Heat and humidity", description: "Tropical conditions demand strict cold chain management. Load catering with AC running and inspect all deliveries carefully." },
      { title: "Security awareness", description: "Follow your operator's security protocols rigorously. Use vetted ground transport providers. Coordinate through the FBO." },
      { title: "Cultural sensitivity", description: "Nigerian hospitality is warm and generous. Respect local customs and business culture. Many clients appreciate traditional Nigerian cuisine elements." }
    ]
  ),
  airportTemplate(
    "hong-kong-vhhh-private-jet", "Hong Kong", "VHHH", "HKG",
    "VHHH — Asia's financial capital meets one of the world's most complex aviation environments. Premier business aviation with extraordinary gastronomy.",
    [
      "Hong Kong International Airport handles private aviation alongside one of the world's busiest commercial operations. The business aviation terminal serves Asia's financial elite with world-class facilities.",
      "Hong Kong's position as a global culinary capital means catering options are exceptional — Cantonese dim sum, Japanese cuisine, international fine dining, all at extraordinary quality."
    ],
    [
      "Hong Kong's airspace is one of the most complex in the world. Slot management is critical, and business aviation operations must be coordinated well in advance through the dedicated business aviation centre.",
      "The catering market reflects Hong Kong's status as a global food destination. Virtually any cuisine at any quality level is available — often at short notice."
    ],
    [
      "ICAO: VHHH | IATA: HKG",
      "One of the world's busiest airports — strict slot management",
      "Dedicated business aviation centre (HKBA)",
      "Extraordinary culinary diversity: Cantonese, Japanese, international",
      "Year-round subtropical climate — heat management in summer",
      "30 minutes from Central Hong Kong (traffic-dependent)",
      "Complex regulatory environment — advance planning essential"
    ],
    "Navigating Hong Kong's Complexity",
    [
      "Hong Kong aviation regulations are strict and precisely enforced. Every aspect of your operation — from landing slots to parking to catering delivery — must be coordinated in advance through proper channels.",
      "The upside is that Hong Kong's infrastructure is world-class. Once you're in the system, everything operates with exceptional efficiency."
    ],
    [
      { title: "Cantonese culinary excellence", description: "Dim sum for breakfast/lunch departures is spectacular. Hong Kong's Cantonese cuisine is among the world's finest — leverage it." },
      { title: "Advance slot planning", description: "Hong Kong slots are competitive. File early, confirm through your handling agent, and have contingency plans." },
      { title: "Typhoon season", description: "June to October brings typhoon risk. Monitor weather closely and have diversion plans. Signal 8 or above grounds all operations." },
      { title: "Dietary diversity", description: "Hong Kong's international community means diverse dietary needs. Local caterers are experienced with every major dietary requirement." }
    ]
  ),
  airportTemplate(
    "tokyo-rjaa-private-jet", "Tokyo Narita", "RJAA", "NRT",
    "RJAA — Japan's international gateway. Japanese precision, exceptional gastronomy, and a business aviation market built on respect and detail.",
    [
      "Tokyo Narita Airport serves as the primary international business aviation gateway to Japan. Japanese aviation operations reflect the country's legendary attention to detail, precision, and service excellence.",
      "Japanese catering for private aviation is an experience unto itself — bento boxes, sushi, wagyu beef, and seasonal kaiseki adapted for in-flight service."
    ],
    [
      "Japanese aviation regulations are precise and must be followed exactly. Business aviation handling at Narita is professional and efficient, but requires advance coordination through authorized handling agents.",
      "The Japanese approach to food — seasonality, presentation, balance — translates beautifully to in-flight catering when sourced from quality Japanese caterers."
    ],
    [
      "ICAO: RJAA | IATA: NRT",
      "Japan's primary international aviation gateway",
      "Business aviation handling through authorized agents",
      "Japanese precision: exact adherence to procedures required",
      "Exceptional culinary tradition: sushi, kaiseki, bento, wagyu",
      "60-90 minutes from central Tokyo (traffic and time of day dependent)",
      "Seasonal awareness: cherry blossom, autumn colours affect travel patterns"
    ],
    "The Japanese Standard of Service",
    [
      "Japanese clients expect a level of attention to detail that exceeds almost any other culture. Presentation matters enormously — how food is arranged, how beverages are served, how the cabin is maintained. Every detail is noticed.",
      "Crew operating in Japan should study Japanese service culture. The concept of 'omotenashi' (wholehearted hospitality) is the standard to which you'll be held."
    ],
    [
      { title: "Japanese cuisine mastery", description: "Bento boxes, premium sushi, wagyu beef, matcha service. Japanese cuisine is perfect for in-flight service — beautiful, balanced, and travel-friendly." },
      { title: "Seasonal awareness", description: "Japanese culture is deeply seasonal. Cherry blossom season, autumn colours, and seasonal ingredients should influence your catering choices." },
      { title: "Presentation perfection", description: "Japanese aesthetics demand perfect presentation. Every element must be intentional. Study Japanese plating and presentation principles." },
      { title: "Cultural protocols", description: "Remove shoes if visiting Japanese homes or certain restaurants. Use both hands when exchanging business cards. Small gestures of cultural respect matter enormously." }
    ]
  ),
  airportTemplate(
    "sydney-yssy-private-jet", "Sydney", "YSSY", "SYD",
    "YSSY — Australia's premier international gateway. World-class facilities, exceptional produce, and the unique challenges of Southern Hemisphere operations.",
    [
      "Sydney Kingsford Smith Airport serves as Australia's primary international business aviation gateway. The airport handles significant private jet traffic, serving Australia's mining magnates, tech entrepreneurs, and international UHNW visitors.",
      "Australian catering leverages some of the world's finest raw ingredients: premium seafood, grass-fed beef, extraordinary wines, and a vibrant multicultural food scene."
    ],
    [
      "Sydney operations are well-organized for business aviation, with dedicated handling and professional FBO services. The long distances involved in Australian and trans-Pacific travel mean ultra-long-range planning is essential.",
      "The Australian culinary scene draws from Asian, European, and indigenous influences, creating a unique and high-quality catering landscape."
    ],
    [
      "ICAO: YSSY | IATA: SYD",
      "Australia's primary international aviation hub",
      "Professional business aviation handling",
      "World-class produce: seafood, beef, wines",
      "Multicultural culinary scene: Asian, European, Indigenous influences",
      "20 minutes from CBD (traffic-dependent)",
      "Ultra-long-range operations: plan for 14+ hour flights to Europe/US",
      "Strict biosecurity: no food items can be brought into Australia"
    ],
    "The Distance Factor",
    [
      "Australia's geographic isolation means most international flights are ultra-long-range. A Sydney to London flight is 17+ hours. This demands exceptional multi-service catering planning and crew endurance management.",
      "Australia's strict biosecurity laws prohibit bringing food items into the country. All catering for departures must be sourced locally."
    ],
    [
      { title: "Australian produce excellence", description: "Leverage Australia's exceptional raw ingredients: Sydney rock oysters, Tasmanian salmon, grass-fed beef, and world-class wines from Barossa Valley, Hunter Valley, and Margaret River." },
      { title: "Biosecurity compliance", description: "No food may be brought into Australia. All catering for outbound flights must be sourced locally. Plan accordingly when arriving." },
      { title: "Ultra-long-range planning", description: "Flights to Europe or US are 14-17+ hours. Plan 4-5 service windows, crew rest, and food freshness across the entire duration." },
      { title: "Asian fusion advantage", description: "Sydney's Asian-Australian fusion cuisine is world-class. Thai, Vietnamese, Japanese, and Chinese influences create unique catering opportunities." }
    ]
  )
];
